import React from 'react';
import { motion } from 'framer-motion';
import { Github, BookOpen } from 'lucide-react';
import { certifications } from '../mock';

const Certifications = () => {
  return (
    <section id="certifications" className="relative py-32 bg-background overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-20 pointer-events-none" />
      <div
        className="absolute bottom-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-[0.06] blur-[120px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #f4a261 0%, transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <span className="mono text-xs uppercase tracking-widest text-primary mb-4 block">// 05 — Certifications</span>
            <h2 className="serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1]">
              Credentials,<br />
              <span className="italic text-slate-500">earned & current</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 flex items-end">
            <p className="text-slate-400 text-lg leading-relaxed">
              I treat certifications as a forcing function for staying sharp — not a wall of badges. Each one below was earned with hands-on labs and is renewed on schedule.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {certifications.map((cert, i) => (
            <motion.div
              key={cert.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="group rounded-2xl overflow-hidden bg-card border border-white/5 hover:border-primary/40 transition-all duration-500 hover:shadow-xl hover:shadow-primary/10"
            >
              {/* Badge area */}
              <div className="relative h-44 flex items-center justify-center overflow-hidden bg-gradient-to-br from-white/5 to-white/[0.02]">
                <div className="absolute inset-0 bg-grid-dark opacity-30" />
                <div
                  className="absolute -top-10 -right-10 w-44 h-44 rounded-full opacity-20 blur-3xl pointer-events-none"
                  style={{ background: 'radial-gradient(circle,#d97757,transparent 70%)' }}
                />
                {/* Index number */}
                <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest text-slate-600">
                  {String(i + 1).padStart(2, '0')}
                </div>
                {/* Active badge */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="mono text-[9px] uppercase tracking-widest text-emerald-400">Active</span>
                </div>

                {cert.badge ? (
                  <img
                    src={cert.badge}
                    alt={cert.name}
                    className="relative h-32 w-auto object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-lg"
                    draggable={false}
                  />
                ) : (
                  <FallbackBadge code={cert.code} issuer={cert.issuer} />
                )}
              </div>

              {/* Text */}
              <div className="p-6 border-t border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <span className="mono text-xs uppercase tracking-widest text-primary font-semibold">
                    {cert.code}
                  </span>
                </div>
                <h3 className="text-white font-semibold text-sm leading-snug mb-4 min-h-[2.5rem]">
                  {cert.name}
                </h3>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="mono text-[10px] uppercase tracking-widest text-slate-600">Issuer</span>
                  <span className="text-sm font-medium text-slate-300">{cert.issuer}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FallbackBadge = ({ code, issuer }) => {
  const isGithub = issuer === 'GitHub';
  return (
    <div
      className={`relative w-28 h-32 flex flex-col items-center justify-center rounded-2xl shadow-lg border border-white/10 ${
        isGithub
          ? 'bg-gradient-to-b from-[#161b22] to-[#0d1117]'
          : 'bg-gradient-to-b from-[#2a1f1a] to-[#1c1917]'
      }`}
    >
      <div className="absolute inset-x-2 top-2 h-0.5 rounded-full bg-white/10" />
      <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-2">
        {isGithub ? (
          <Github size={20} className="text-white" />
        ) : (
          <BookOpen size={20} className="text-primary" />
        )}
      </div>
      <div className="mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">{issuer}</div>
      <div className="mono text-xs font-bold text-white">{code}</div>
    </div>
  );
};

export default Certifications;
